// @flow

import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import moment from "moment";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/reporting/actionCreator";
import {
  GetSupplierNewReferrals,
  GetSupplierActiveReferrals,
  GetSupplierClosedReferrals,
  GetSupplierReferralDetails,
  SubmitSupplierReferralStatus,
  GetSupplierClosedReferralsStatuses,
  GetSupplierClosedReferralsExport,
  GetRTWReferralDetails,
  SubmitRTWReferralData,
  GetLogReferralDetails,
  SubmitLogReferralData,
  GetReferralOutcomesReport,
  ExportReferralOutcomesReport,
  GetManagerKpiReport,
  ExportManagerKpiReport,
} from "@modules/reporting/constants/actions";
import { SUPPLIER_REFERRAL_REPORT_TYPE, SUPPLIER_REFERRAL_STATUSES } from "../../constants/reports";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type {
  OutcomeReportType,
  ClosedReferralsFilters,
  OutcomesReportFiltersType,
  ManagerKpiFilters,
} from "@modules/reporting/reducer/referral-tracker/types";
import { downloadCsvFile } from "@sagas/utils";

const URL_PATH = (clientId, page) =>
  `/api/v1/reports/client/${clientId}/reports/supplier-referral-tasks-report?limit=${
    config.shortResultsPerPage
  }&page=${page || 1}`;

type ReferralsRequest = $Exact<{
  clientId: number,
  page?: number,
  filters?: ClosedReferralsFilters,
}>;

type OutcomesPayload = $Exact<{
  clientId: number,
  filters: OutcomesReportFiltersType,
  type: OutcomeReportType,
}>;

type ManagerKpiPayload = $Exact<{
  clientId: number,
  filters?: ManagerKpiFilters,
  page?: number,
}>;

export function* onGetSupplierNewReferrals({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(http.post, URL_PATH(payload.clientId, payload.page), {
      supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.NEW_REFERRALS,
      referralProcessId: payload.filters && payload.filters.referralProcessId,
      referralType: payload.filters && payload.filters.referralType,
    });

    yield put(actionCreators.GetSupplierNewReferralsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSupplierNewReferralsError(e.response));
  }
}

export function* onGetSupplierActiveReferrals({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(http.post, URL_PATH(payload.clientId, payload.page), {
      supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.ACTIVE_REFERRALS,
      referralProcessId: payload.filters && payload.filters.referralProcessId,
      referralType: payload.filters && payload.filters.referralType,
    });

    yield put(actionCreators.GetSupplierActiveReferralsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSupplierActiveReferralsError(e.response));
  }
}

export function* onGetSupplierClosedReferrals({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(http.post, URL_PATH(payload.clientId, payload.page), {
      supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.CLOSED_REFERRALS,
      status: (payload.filters && payload.filters.status) || SUPPLIER_REFERRAL_STATUSES.ALL,
      from_date: (payload.filters && payload.filters.fromDate) || null,
      to_date: (payload.filters && payload.filters.toDate) || null,
      referralProcessId: payload.filters && payload.filters.referralProcessId,
      referralType: payload.filters && payload.filters.referralType,
    });

    yield put(actionCreators.GetSupplierClosedReferralsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsError(e.response));
  }
}

type ReferralDetailsRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
  referralType: string,
}>;

type ReferralStatusRequest = $Exact<{
  ...ReferralDetailsRequest,
  supplierReferralStatus: string,
}>;

export function* onGetSupplierReferralDetails({
  payload,
}: Action<ReferralDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.supplierReferralId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/supplier-referral-tasks-report/${payload.supplierReferralId}`,
    );

    yield all([
      put(actionCreators.GetSupplierReferralDetailsSuccess(response.data)),
      put(
        actionCreators.ToggleSupplierReferralDetailsModal({
          supplierReferralId: payload.supplierReferralId,
          referralType: payload.referralType,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetSupplierReferralDetailsError(e.response));
  }
}
//RTW
export function* onGetRTWReferralDetails({
  payload,
}: Action<ReferralDetailsRequest>): Generator<*, void, *> {
  if (!payload) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/rtw-and-fit-date`,
    );

    yield all([
      put(actionCreators.GetRTWReferralDetailsSuccess(response.data)),
      put(actionCreators.ToggleRTWReferralDetailsModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.GetRTWReferralDetailsError(e.response));
  }
}
export function* onSubmitRTWReferralData({
  payload,
}: Action<ReferralLogPostRequest>): Generator<*, void, *> {
  if (!payload) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/rtw-and-fit-date`,
      { ...payload.requestBody },
    );

    yield all([
      put(
        actionCreators.SubmitRTWReferralDataSuccess({
          supplierReferralId: payload.supplierReferralId,
          rtwDate: response.data.rtwDate,
        }),
      ),
      put(actionCreators.ToggleRTWReferralDetailsModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitRTWReferralDataError(e.response));
  }
}
// Log
type ReferralLogDetailsRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
}>;
export function* onGetLogReferralDetails({
  payload,
}: Action<ReferralLogDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.supplierReferralId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/get-treatment-logs`,
    );
    yield getOutcomeOptions({
      type: "",
      payload: {
        clientId: payload.clientId,
        supplierReferralId: payload.supplierReferralId,
      },
    });

    yield all([
      put(actionCreators.GetLogReferralDetailsSuccess(response.data)),
      put(actionCreators.ToggleLogReferralDetailsModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.GetLogReferralDetailsError(e.response));
  }
}
type ReferralLogPostRequest = $Exact<{
  clientId: number,
  supplierReferralId: number,
  requestBody: Object,
}>;
export function* onSubmitLogReferralData({
  payload,
}: Action<ReferralLogPostRequest>): Generator<*, void, *> {
  const { clientId, supplierReferralId, requestBody } = payload || {};
  if (!clientId || !supplierReferralId || !requestBody) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `api/v1/reports/client/${clientId}/reports/referral-details/supplier/task/${supplierReferralId}/add-treatment-log`,
      { ...requestBody },
    );

    yield all([put(actionCreators.SubmitLogReferralDataSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.SubmitLogReferralDataError(e.response));
  }
}

export function* getOutcomeOptions({
  payload,
}: Action<ReferralLogDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.supplierReferralId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/reports/referral-details/supplier/task/${payload.supplierReferralId}/get-outcome-options`,
    );
    yield all([
      put(
        actionCreators.SetReferralCommonData({
          outcomeOptions: response.data,
          supplierReferralId: payload.supplierReferralId,
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetLogReferralOutcomeOptionsError(e.response));
  }
}
// Status

export function* onSubmitSupplierReferralStatus({
  payload,
}: Action<ReferralStatusRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.supplierReferralId ||
    !payload.supplierReferralStatus
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.put,
      `/api/v1/tools/client/${payload.clientId}/tools/task/supplier-referral-tasks/${payload.supplierReferralId}`,
      {
        supplier_referral_status: payload.supplierReferralStatus,
      },
    );

    yield all([
      put(actionCreators.SubmitSupplierReferralStatusSuccess()),
      put(
        actionCreators.GetSupplierNewReferrals({
          clientId: payload.clientId,
        }),
      ),
      put(
        actionCreators.GetSupplierActiveReferrals({
          clientId: payload.clientId,
        }),
      ),
      put(
        actionCreators.GetSupplierClosedReferrals({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("REFERRAL_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitSupplierReferralStatusError(e.response));
  }
}

type ClosedReferralStatusesRequest = $Exact<{
  clientId: number,
}>;

export function* onGetSupplierClosedReferralsStatuses({
  payload,
}: Action<ClosedReferralStatusesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/supplier-referral-tasks/statuses`,
    );

    yield all([put(actionCreators.GetSupplierClosedReferralsStatusesSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsStatusesError(e.response));
  }
}

export function* onGetSupplierClosedReferralsExport({
  payload,
}: Action<ReferralsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const todaysDate = moment().format("YYYY-MM-DD");
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/supplier-referral-tasks-report/export`,
      {
        supplier_referral_tasks_report_type: SUPPLIER_REFERRAL_REPORT_TYPE.CLOSED_REFERRALS,
        status:
          (payload.filters && payload.filters.status && payload.filters.status) ||
          SUPPLIER_REFERRAL_STATUSES.ALL,
        from_date: (payload.filters && payload.filters.fromDate) || null,
        to_date: (payload.filters && payload.filters.toDate) || null,
        referralProcessId: payload.filters && payload.filters.referralProcessId,
        referralType: payload.filters && payload.filters.referralType,
      },
    );

    downloadCsvFile(
      response.data,
      `${(payload.filters && payload.filters.fromDate) || todaysDate}_${(payload.filters &&
        payload.filters.toDate) ||
        todaysDate}_closed_supplier_referral_export_.csv`,
    );

    yield all([put(actionCreators.GetSupplierClosedReferralsExportSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetSupplierClosedReferralsExportError(e.response));
  }
}

export function* onGetManagerKpiReport({
  payload,
}: Action<ManagerKpiPayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/manager/kpi`,
      {
        params: {
          ...payload.filters,
          page: payload.page || 1,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreators.GetManagerKpiReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetLogReferralDetailsError(e.response));
  }
}

export function* onExportManagerKpiReport({
  payload,
}: Action<ManagerKpiPayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/manager/kpi/export`,
      {
        params: payload.filters,
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportManagerKpiReportSuccess());
    yield downloadCsvFile(response.data, "manager-kpi-report.csv");
  } catch (e) {
    yield put(actionCreators.ExportManagerKpiReportError(e.response));
  }
}

export function* onGetReferralOutcomesReport({
  payload,
}: Action<OutcomesPayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.type) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral/outcome`,
      {
        params: {
          type: payload.type,
          ...payload.filters,
        },
      },
    );

    yield put(actionCreators.GetReferralOutcomesReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReferralOutcomesReportError(e.response));
  }
}

export function* onExportReferralOutcomesReport({
  payload,
}: Action<OutcomesPayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.type) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/referral/outcome/export`,
      {
        params: {
          type: payload.type,
          ...payload.filters,
        },
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportReferralOutcomesReportSuccess());
    yield downloadCsvFile(response.data, "referral-outcomes-report.csv");
  } catch (e) {
    yield put(actionCreators.ExportReferralOutcomesReportError(e.response));
  }
}

export function* ReferralsTrackerSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetSupplierNewReferrals, onGetSupplierNewReferrals),
    takeEvery(GetSupplierActiveReferrals, onGetSupplierActiveReferrals),
    takeEvery(GetSupplierClosedReferrals, onGetSupplierClosedReferrals),
    takeEvery(GetSupplierReferralDetails, onGetSupplierReferralDetails),
    takeEvery(SubmitSupplierReferralStatus, onSubmitSupplierReferralStatus),
    takeEvery(GetRTWReferralDetails, onGetRTWReferralDetails),
    takeEvery(SubmitRTWReferralData, onSubmitRTWReferralData),
    takeEvery(GetLogReferralDetails, onGetLogReferralDetails),
    takeEvery(SubmitLogReferralData, onSubmitLogReferralData),
    takeEvery(GetSupplierClosedReferralsStatuses, onGetSupplierClosedReferralsStatuses),
    takeEvery(GetSupplierClosedReferralsExport, onGetSupplierClosedReferralsExport),
    takeEvery(GetManagerKpiReport, onGetManagerKpiReport),
    takeEvery(ExportManagerKpiReport, onExportManagerKpiReport),
    takeEvery(GetReferralOutcomesReport, onGetReferralOutcomesReport),
    takeEvery(ExportReferralOutcomesReport, onExportReferralOutcomesReport),
  ]);
}

export default ReferralsTrackerSaga;
